import React, {forwardRef, useState} from 'react'
// import {Box, chakra, Flex, Input, useMultiStyleConfig, useMediaQuery} from '@chakra-ui/react'
import {Box, chakra, Flex, Input, useMultiStyleConfig} from '@chakra-ui/react'
import ReactDatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import PropTypes from 'prop-types'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {localeMap} from './date-picker-utils'
import {remCalc} from '../../../../overrides/app/theme/_utils'

export function getDateFnsLocale(siteLocale) {
    if (!siteLocale) return 'en-GB'
    const normalizedLocale = siteLocale.toLowerCase()

    if (localeMap[normalizedLocale]) {
        return localeMap[normalizedLocale]
    }
    return 'en-GB' //fallback
}

export function getStatus({isSuccess, isDisabled}) {
    let status = null
    status ??= isSuccess && 'success'
    status ??= !isSuccess && 'error'
    if (isDisabled) {
        status = 'disabled'
    }
    return status
}

const ChakraDatePicker = chakra(ReactDatePicker)
export const CustomInput = forwardRef(
    ({value, onClick, placeholder, calendarIcon, styles, isCalendarOpen, clearIcon}, ref) => {
        const hasValue = Boolean(value)
        const [isInputFocused, setIsInputFocused] = useState(false)
        const labelShouldMove = !hasValue && (!isCalendarOpen || !isInputFocused)

        return (
            <Flex {...styles?.customInputContainer} position="relative">
                <Box
                    {...(styles?.[labelShouldMove ? 'defaultLabel' : 'activeLabel'] ?? {})}
                    left={remCalc(16)}
                    zIndex="1"
                    position="absolute"
                    pointerEvents="none"
                    transition="all 0.2s ease"
                    data-testid="placeholder-label"
                    transform={labelShouldMove ? `translateY(1px)` : `translateY(-22px)`}
                >
                    {placeholder}
                </Box>

                <Input
                    ref={ref}
                    value={value}
                    onClick={onClick}
                    placeholder=""
                    readOnly
                    onFocus={() => {
                        setIsInputFocused(true)
                    }}
                    onBlur={() => setIsInputFocused(false)}
                    data-testid="input"
                    {...styles?.customInput}
                />
                {!clearIcon && (
                    <Box {...styles?.calendarIconContainer} data-testid="calendar-icon">
                        {calendarIcon}
                    </Box>
                )}
            </Flex>
        )
    },
)

CustomInput.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    onClick: PropTypes.func,
    placeholder: PropTypes.string,
    calendarIcon: PropTypes.node,
    styles: PropTypes.object,
    isCalendarOpen: PropTypes.bool,
    clearIcon: PropTypes.bool,
}

CustomInput.displayName = 'CustomInput'

const DatePicker = forwardRef(
    (
        {
            isDisabled,
            onChange,
            value,
            isEditForm,
            placeholder,
            maxDate,
            calendarIcon,
            isSuccess,
            clearIcon,
            ...rest
        },
        // eslint-disable-next-line
        ref,
    ) => {
        //const [isMobile] = useMediaQuery(`(max-width: ${remCalc(449)})`)
        const status = getStatus({isSuccess, isDisabled})
        const styles = useMultiStyleConfig('DatePicker', {variant: status})
        const {locale} = useMultiSite()
        const [isCalendarOpen, setIsCalendarOpen] = useState(false)
        return (
            <Box __css={styles} w="100%">
                <Flex {...styles?.container}>
                    <ChakraDatePicker
                        onChange={onChange}
                        selected={value}
                        locale={getDateFnsLocale(locale?.id)}
                        dateFormat="P" // 'P' auto-adapts based on locale
                        //dateFormat="dd/MM/yyyy"
                        disabled={isEditForm || isDisabled}
                        placeholderText={placeholder}
                        maxDate={maxDate}
                        customInput={
                            <CustomInput
                                isCalendarOpen={isCalendarOpen}
                                styles={styles}
                                calendarIcon={calendarIcon}
                                clearIcon={clearIcon}
                            />
                        }
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={100}
                        onCalendarOpen={() => setIsCalendarOpen(true)}
                        onCalendarClose={() => setIsCalendarOpen(false)}
                        {...rest}
                    />
                    {/* {!isMobile ? (
                        
                    ) : (
                        <Flex {...styles?.customMobileInputContainer}>
                            <input
                                type="date"
                                value={value ? value.toISOString().split('T')[0] : ''}
                                onChange={(e) => onChange(new Date(e.target.value))}
                                required
                                max={maxDate ? maxDate.toISOString().split('T')[0] : undefined}
                            />
                        </Flex>
                    )} */}
                </Flex>
            </Box>
        )
    },
)

DatePicker.displayName = 'DatePicker'
DatePicker.propTypes = {
    isDisabled: PropTypes.bool,
    onChange: PropTypes.func,
    value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    isEditForm: PropTypes.bool,
    placeholder: PropTypes.string,
    maxDate: PropTypes.object,
    calendarIcon: PropTypes.any,
    isSuccess: PropTypes.bool,
    clearIcon: PropTypes.bool,
}

export default DatePicker
